import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import {
  StatusCodeFilter,
  statusCodeIsPinned,
} from "../statusCodeFilter";
import { SPEED_UNITS } from "../../admin-details/account/constants";
import { convertKmphTo } from "../../../utils/distance";
import {
  ClusterListTrip,
  createClusterAssetListOverlay,
} from "./clusterAssetListOverlay";
import { createClusterLensOverlay } from "./clusterLensOverlay";
import { detectOverlapClusters, OverlapCluster } from "./overlapClusters";
import { TimedPathPoint } from "./resolveRoutePath";
import {
  EventPinFocus,
  eventMarkerIcon,
  PIN_Z_INDEX_BY_FOCUS,
} from "./eventIcons";
import { createSelectedEventPulse } from "./eventPinFocus";
import { createEventStackController } from "./eventStackController";
import { groupIntoRuns, runLabelIcon } from "./eventStacks";
import { formatDuration, isMovingEvent } from "../util/tripSummary";
import { RouteLineStyle } from "./routeLineStyle";
import { TooltipController } from "./tooltip";
import { createRouteHitLayer } from "./routeHitArea";
import { createEventCamera } from "./eventCamera";

// where a point click came from - see onTripPointClick below
export type TripPointClickSource = "route" | "pin";

export type SelectedVehicleTrip = {
  deviceId: string;
  vehicleIndex: number;
  // short - the asset id on its own, for the animated route pill (pixel
  // space on the map is tight; the account's full label often isn't)
  vehicleLabel: string;
  // the account-configured label - what the hover tooltip and the cluster
  // list show, since those are ordinary text UI, not a pixel-constrained pill
  vehicleFullLabel: string;
  // position in this vehicle's trips - the trip's identity, and what its
  // key is built from. tripNumber below is only the label.
  tripIndex: number;
  tripNumber: number;
  color: string;
  // the reported GPS fixes - what the events, tooltips and pins come from
  points: VehicleMapDataPoint[];
  // what actually gets drawn: road-aligned for every route view except
  // point-to-point, where it is the fixes themselves
  path: TimedPathPoint[];
};

const speedUnitSuffix = (unit: SPEED_UNITS) => {
  switch (unit) {
    case SPEED_UNITS.MPH:
      return "mph";
    case SPEED_UNITS.NAUTICAL_MILES:
      return "kn";
    case SPEED_UNITS.KMH:
    default:
      return "km/h";
  }
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// a route can carry thousands of fixes - Mesa reports every 5 seconds - and
// every animation frame re-sets the whole path on every visible route. at a
// full fleet that is millions of point copies per replay, so the animation
// runs on a thinned copy and only snaps to the real path once it lands.
// the eye can't tell at map zoom; the finished route is still exact.
const ANIMATION_MAX_POINTS = 400;

// A selection carrying this many event pins is treated as "heavy": its pins
// are built a chunk per frame instead of in one blocking pass, and they
// arrive as a travelling wave instead of every marker animating at once.
// Below it nothing changes - the single pass and simultaneous drop-in is
// what makes an ordinary selection feel snappy.
const HEAVY_TRIP_PIN_COUNT = 400;
// markers constructed per frame while the routes are drawing
const PIN_BUILD_CHUNK = 150;
// pins brought onto the map per frame once they exist
const PIN_REVEAL_CHUNK = 40;

const thinForAnimation = <T,>(items: T[]): T[] => {
  if (items.length <= ANIMATION_MAX_POINTS) return items;
  const stride = items.length / ANIMATION_MAX_POINTS;
  const thinned: T[] = [];
  for (let i = 0; i < ANIMATION_MAX_POINTS; i++)
    thinned.push(items[Math.floor(i * stride)]);
  // the real last fix, so a route ends where it actually ended
  const last = items[items.length - 1];
  if (thinned[thinned.length - 1] !== last) thinned.push(last);
  return thinned;
};

export const tripKeyFor = (deviceId: string, tripIndex: number) =>
  `${deviceId}__${tripIndex}`;

const buildStrokeOptions = (style: RouteLineStyle) => {
  switch (style) {
    case "dashed":
      return {
        strokeOpacity: 0,
        strokeWeight: 2,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 2 },
            offset: "0",
            repeat: "10px",
          },
        ],
      };
    case "dotted":
      return {
        strokeOpacity: 0,
        strokeWeight: 2,
        icons: [
          {
            icon: { path: "M 0,0 0,0", strokeOpacity: 1, scale: 3 },
            offset: "0",
            repeat: "5px",
          },
        ],
      };
    case "solid_thin":
      return { strokeOpacity: 0.85, strokeWeight: 1.5, icons: [] as any[] };
    case "solid":
    default:
      return { strokeOpacity: 0.85, strokeWeight: 2.5, icons: [] as any[] };
  }
};

const eventLabel = (point: VehicleMapDataPoint) =>
  point.resolvedDescription ||
  point.status?.description ||
  `Status ${point.statuscode}`;

const tooltipHtmlFor = (
  vehicleFullLabel: string,
  tripNumber: number,
  point: VehicleMapDataPoint,
  speedUnit: SPEED_UNITS
) => `
  <div><strong>${vehicleFullLabel} · Trip ${tripNumber}</strong></div>
  <div>${eventLabel(point)}</div>
  <div>${Math.round(convertKmphTo(speedUnit, point.speedkph))} ${speedUnitSuffix(speedUnit)}</div>
`;

const findNearestPoint = (
  points: VehicleMapDataPoint[],
  lat: number,
  lng: number
) => {
  let nearest = points[0];
  let nearestDistSq = Infinity;
  points.forEach((point) => {
    const dLat = point.latitude - lat;
    const dLng = point.longitude - lng;
    const distSq = dLat * dLat + dLng * dLng;
    if (distSq < nearestDistSq) {
      nearestDistSq = distSq;
      nearest = point;
    }
  });
  return nearest;
};

// One hue rather than a traffic-light scale: the map is already carrying a
// colour per vehicle, and a second colour code fighting it reads as noise.
// Violet is a tertiary complement to the map's greens, so it separates from
// the terrain without competing with the route colours. Weight is carried by
// size and depth instead - a busier junction is bigger and darker.
const CLUSTER_HUE = { h: 280, s: 62 };
const CLUSTER_MIN_DIAMETER = 24;
const CLUSTER_MAX_DIAMETER = 42;
// the count at which a badge is as large and as dark as it gets
const CLUSTER_SATURATION_COUNT = 8;

// How far one badge's stretch of shared road may run, expressed in screen
// terms rather than metres. A badge says "these trips share this road here",
// and what counts as "here" is whatever the current view calls a place: a
// corridor across a metro when zoomed out, one junction when zoomed to the
// street. Deriving the span from the zoom is what makes a badge split into
// the several it was standing for as you go in, instead of a single number
// parked on one point at every scale.
//
// Without a span at all - which is where this was - a shared yard chains
// every trip that ever touched it into one component that reaches as far as
// the corridor does, and the overlaps further along the road never get a
// badge of their own because they were absorbed into it.
// There is deliberately no ceiling on this. The zoom term IS the bound -
// it already holds a badge to OVERLAP_SPAN_PIXELS of screen whatever the
// scale - so a metres cap on top of it can only ever fire by overriding
// the span the view asked for. A 50km one used to, from zoom 10 down: at
// zoom 4 a pixel is 7.7km, so 420px wants a 3,239km span and the cap gave
// 50km - a badge every 6 pixels, which carpeted a cross-country fleet in
// thousands of them.
const OVERLAP_SPAN_PIXELS = 420;
const OVERLAP_SPAN_MIN_METERS = 150;

const metersPerPixelAt = (zoom: number, latitude: number) =>
  (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom);

const overlapSpanForZoom = (zoom: number, latitude: number) =>
  Math.max(
    OVERLAP_SPAN_MIN_METERS,
    metersPerPixelAt(zoom, latitude) * OVERLAP_SPAN_PIXELS
  );

const clusterBadgeIcon = (google: any, count: number) => {
  const weight = Math.min(
    1,
    (count - 2) / Math.max(1, CLUSTER_SATURATION_COUNT - 2)
  );
  const diameter = Math.round(
    CLUSTER_MIN_DIAMETER +
      (CLUSTER_MAX_DIAMETER - CLUSTER_MIN_DIAMETER) * weight
  );
  // lighter for a pair, deepening as more trips pile onto the same road
  const lightness = Math.round(62 - 26 * weight);
  const fill = `hsl(${CLUSTER_HUE.h} ${CLUSTER_HUE.s}% ${lightness}%)`;
  const halo = `hsl(${CLUSTER_HUE.h} ${CLUSTER_HUE.s}% ${lightness}% / 0.25)`;
  const center = diameter / 2;
  const fontSize = Math.round(diameter * 0.42);

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}">
      <circle cx="${center}" cy="${center}" r="${center - 0.5}" fill="${halo}" />
      <circle cx="${center}" cy="${center}" r="${center - 4}" fill="${fill}" stroke="white" stroke-width="2" />
      <text x="${center}" y="${center + fontSize * 0.35}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="white" text-anchor="middle">${count}</text>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(diameter, diameter),
    anchor: new google.maps.Point(center, center),
  };
};

// the vehicle name rides the head of its route while it draws. this is what
// identifies the route during animation - colour can't do that job alone
// when several vehicles are unfolding at once and some users are colour blind.
const vehicleLabelIcon = (google: any, label: string, color: string) => {
  const width = Math.max(46, label.length * 8 + 18);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="22" viewBox="0 0 ${width} 22">
      <rect x="0.5" y="0.5" rx="11" ry="11" width="${width - 1}" height="21" fill="${color}" stroke="white" stroke-width="1.5" />
      <text x="${width / 2}" y="15" font-family="Arial, sans-serif" font-size="11" font-weight="700" fill="white" text-anchor="middle">${label}</text>
    </svg>
  `;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(width, 22),
    anchor: new google.maps.Point(width / 2, 11),
  };
};

// draws every checked trip, coloured per vehicle. replays run on a shared
// clock so vehicles that were driving at the same time unfold together.
//
// generic over what "one trip" means - the multi-vehicle map keys entries by
// deviceId, the asset map's compare view keys them by dateKey. neither
// meaning is baked in here; deviceId is just an opaque grouping id.
export const renderMultiVehicleRoutes = ({
  map,
  trips,
  lineStyle,
  tooltip,
  speedUnit = SPEED_UNITS.KMH,
  timezone,
  onHoverTrip,
  onTripPointClick,
  onClusterTripSelect,
}: {
  map: any;
  trips: SelectedVehicleTrip[];
  lineStyle: RouteLineStyle;
  tooltip: TooltipController;
  // the account's configured speed unit - the hover tooltip's only unit
  // conversion, so it matches every other speed shown in the app
  speedUnit?: SPEED_UNITS;
  // the account's timezone, for the times on the stacked-event list
  timezone?: string;
  onHoverTrip?: (tripKey: string | null) => void;
  // `source` is what the user actually clicked. A route click is a request
  // to open that trip; an event pin click is a request to read one event on
  // a trip already open, and must not disturb what is on the map around it.
  onTripPointClick?: (
    tripKey: string,
    point: VehicleMapDataPoint,
    source?: TripPointClickSource
  ) => void;
  // picking a trip out of an overlap badge - opens that whole asset with
  // this trip singled out, rather than the single trip a route click gives
  onClusterTripSelect?: (tripKey: string) => void;
}) => {
  const google = (window as any).google;
  let cancelled = false;

  let currentLineStyle = lineStyle;
  let strokeIcons = buildStrokeOptions(currentLineStyle).icons;

  const routeHitLayer = createRouteHitLayer({ map });

  const entries = trips
    .filter((trip) => trip.points.length >= 2 && trip.path.length >= 2)
    .map((trip) => {
      const rawPath = trip.path.map((point) => ({
        lat: point.lat,
        lng: point.lng,
      }));
      const strokeOptions = buildStrokeOptions(lineStyle);
      const tripKey = tripKeyFor(trip.deviceId, trip.tripIndex);

      // the cursor is caught by a wide transparent line paired with this
      // one, and the layer hands the event to whichever route is nearest -
      // see routeHitArea
      const polyline = routeHitLayer.createRouteLine({
        tripKey,
        visibleOptions: {
          path: [],
          map,
          strokeColor: trip.color,
          strokeOpacity: strokeOptions.strokeOpacity,
          strokeWeight: strokeOptions.strokeWeight,
          icons: strokeOptions.icons,
        },
        handlers: {
          onEnter: () => {
            // Deliberately does NOT close the cluster list. Reaching that
            // popup means dragging the cursor off the badge and across
            // whatever lies between, and on a busy day that is always a
            // route - so closing on hover made the list unreachable, it
            // vanished on the way to it. A click is the signal that you
            // have moved on; see onClick.
            onHoverTrip?.(tripKey);
          },
          onMove: (point, domEvent) => {
            const nearest = findNearestPoint(
              trip.points,
              point.lat,
              point.lng
            );
            tooltip.show(
              tooltipHtmlFor(
                trip.vehicleFullLabel,
                trip.tripNumber,
                nearest,
                speedUnit
              ),
              domEvent.clientX,
              domEvent.clientY
            );
          },
          onLeave: () => {
            tooltip.hide();
            onHoverTrip?.(null);
          },
          onClick: (point) => {
            // picking a route is leaving the badge behind
            closeLens();
            onTripPointClick?.(
              tripKey,
              findNearestPoint(trip.points, point.lat, point.lng),
              "route"
            );
          },
        },
      });

      // index-aligned: animPath[i] is the position at animTimestamps[i]
      const animPoints = thinForAnimation(trip.path);
      const animPath = animPoints.map((point) => ({
        lat: point.lat,
        lng: point.lng,
      }));
      const animTimestamps = animPoints.map((point) => point.timestamp);

      return {
        tripKey,
        trip,
        polyline,
        rawPath,
        animPath,
        animTimestamps,
        color: trip.color,
      };
    });

  const entryByTripKey = new Map(entries.map((entry) => [entry.tripKey, entry]));

  // detail view can hold several trips at once - one asset's whole day, or a
  // hand-picked set across vehicles - so focus is a set rather than one key
  let detailTripKeys = new Set<string>();
  // one trip singled out from within the detail set - the rest of the set
  // drops back so a single route can be read on its own
  let activeDetailTripKey: string | null = null;
  let highlightedTripKey: string | null = null;
  // routes currently taken off the map by detail view or by isolating
  // another one
  const hiddenTripKeys = new Set<string>();
  // hovering has to read instantly from across the map, so the routes that
  // aren't hovered drop most of the way out rather than merely fading
  const HOVER_DIM_FACTOR = 0.15;

  const applyRouteStyling = () => {
    const baseOptions = buildStrokeOptions(currentLineStyle);
    const inDetailMode = detailTripKeys.size > 0;
    const hasActiveInSet =
      activeDetailTripKey !== null && detailTripKeys.has(activeDetailTripKey);

    entries.forEach((entry) => {
      const isHovered = entry.tripKey === highlightedTripKey;
      const isInSet = detailTripKeys.has(entry.tripKey);
      const isActive = hasActiveInSet && entry.tripKey === activeDetailTripKey;

      // strongest first: a hovered route always wins over everything else,
      // including its own detail-set membership. Checking inDetailMode
      // first would swallow hovering whenever the whole selection is one
      // big set (a vehicle's whole day, a compare card's several trips) -
      // every member of that set would stay at full opacity regardless of
      // hover, since "in the set" and "not hovered" both read as "keep at
      // 1". So: hovered wins, then hover-elsewhere dims everything else
      // uniformly. Routes outside an open detail set aren't dimmed at all -
      // they come off the map below.
      let dimFactor = 1;
      if (isHovered) dimFactor = 1;
      else if (highlightedTripKey !== null) dimFactor = HOVER_DIM_FACTOR;

      // detail view takes everything outside the open set off the map -
      // other vehicles' routes left faintly underneath read as part of the
      // trip being replayed - and singling one trip out takes the rest of
      // the set off too, since dimming isn't enough when they share a
      // corridor. styling re-runs on every hover, so only actual changes
      // are pushed.
      const hidden =
        (inDetailMode && !isInSet) || (hasActiveInSet && isInSet && !isActive);
      if (hidden !== hiddenTripKeys.has(entry.tripKey)) {
        entry.polyline.setMap(hidden ? null : map);
        if (hidden) hiddenTripKeys.add(entry.tripKey);
        else hiddenTripKeys.delete(entry.tripKey);
      }

      const isEmphasised = isHovered || isActive;
      const weight = isEmphasised
        ? baseOptions.strokeWeight + 3
        : baseOptions.strokeWeight;
      const zIndex = isHovered ? 300 : isActive ? 250 : isInSet ? 150 : 1;
      // in detail view only the trips in the open set stay interactive
      const clickable = !inDetailMode || isInSet;

      const baseOpacity = isEmphasised ? 1 : baseOptions.strokeOpacity;

      // Both properties go on every time. Setting only one leaves the other
      // behind from the previous style: switching to dashed or dotted used
      // to add the symbols but leave the line underneath still opaque, so
      // the route stayed solid, and switching back left the old symbols
      // sitting on the solid line.
      const hasIcons = baseOptions.icons.length > 0;
      entry.polyline.setOptions({
        strokeWeight: weight,
        // an icon style hides the line itself - the symbols carry the
        // colour, and emphasis rides on them rather than on the line
        strokeOpacity: hasIcons ? 0 : baseOpacity * dimFactor,
        zIndex,
        clickable,
        icons: hasIcons
          ? baseOptions.icons.map((iconDef) => ({
              ...iconDef,
              icon: {
                ...iconDef.icon,
                strokeOpacity: (iconDef.icon.strokeOpacity ?? 1) * dimFactor,
              },
            }))
          : [],
      });
    });
  };

  const setHighlightedTrip = (tripKey: string | null) => {
    highlightedTripKey = tripKey;
    applyRouteStyling();
  };

  const setLineStyle = (nextLineStyle: RouteLineStyle) => {
    currentLineStyle = nextLineStyle;
    strokeIcons = buildStrokeOptions(currentLineStyle).icons;
    applyRouteStyling();
  };

  // a single arrowhead riding the leading tip of a line as it draws. only
  // used in detail view - during the multi-vehicle draw the head carries
  // the vehicle label pill instead, since that has to say *which* vehicle.
  const arrowHeadIcon = (color: string) => ({
    icon: {
      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 3,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: color,
    },
    offset: "100%",
  });

  // --- focused-trip event pins, same icons as the single-day trip view

  // pins carry their point index so the draw-in can reveal them as the
  // route reaches them instead of dropping every pin at once
  type DetailPin = {
    marker: any;
    tripKey: string;
    pointIndex: number;
    shown: boolean;
    // kept so the pin can be re-iconed at a different size without having
    // to go back to the trip to find out what event it is
    point: VehicleMapDataPoint;
    focus: EventPinFocus;
    
    runPoints?: VehicleMapDataPoint[];
    runLabelMarker?: any;
    runLabel?: string;
  };
  let detailPins: DetailPin[] = [];

  
  const selectedPulse = createSelectedEventPulse(map);
  let hoveredPin: DetailPin | null = null;
  let selectedPin: DetailPin | null = null;

  // The duration pill of a collapsed run is a second marker riding beside
  // its pin, so everything that shows or hides the pin has to move both.
  const setPinOnMap = (pin: DetailPin, on: boolean) => {
    pin.marker.setMap(on ? map : null);
    pin.runLabelMarker?.setMap(on ? map : null);
  };

  const applyPinFocus = (pin: DetailPin, focus: EventPinFocus) => {
    if (pin.focus === focus) return;
    pin.focus = focus;
    pin.marker.setIcon(eventMarkerIcon(pin.point, focus));
    pin.marker.setZIndex(PIN_Z_INDEX_BY_FOCUS[focus]);
    // the pill belongs to this pin, so it steps out as the pin grows and
    // comes forward with it rather than being left under the stack
    if (pin.runLabelMarker) {
      pin.runLabelMarker.setIcon(
        runLabelIcon(pin.runLabel ?? "", focus)
      );
      pin.runLabelMarker.setZIndex(PIN_Z_INDEX_BY_FOCUS[focus]);
    }
  };

  
  let selectedOverlayMarker: any = null;
  const showSelectedOverlay = (point: VehicleMapDataPoint) => {
    const position = { lat: point.latitude, lng: point.longitude };
    if (!selectedOverlayMarker) {
      selectedOverlayMarker = new google.maps.Marker({
        position,
        map,
        // the pin underneath stays the thing you interact with
        clickable: false,
        optimized: false,
        icon: eventMarkerIcon(point, "selected"),
        zIndex: PIN_Z_INDEX_BY_FOCUS.selected,
      });
      return;
    }
    selectedOverlayMarker.setPosition(position);
    selectedOverlayMarker.setIcon(eventMarkerIcon(point, "selected"));
    selectedOverlayMarker.setMap(map);
  };
  const hideSelectedOverlay = () => selectedOverlayMarker?.setMap(null);

  // selection outranks hover, so a pin that is both stays at its selected
  // size instead of shrinking when the pointer lands on it
  const refreshPinFocus = (pin: DetailPin | null) => {
    if (!pin) return;
    applyPinFocus(
      pin,
      pin === selectedPin ? "selected" : pin === hoveredPin ? "hover" : "none"
    );
  };

  const setHoveredPin = (pin: DetailPin | null) => {
    if (hoveredPin === pin) return;
    const previous = hoveredPin;
    hoveredPin = pin;
    refreshPinFocus(previous);
    refreshPinFocus(pin);
  };



  // the open selection is being torn down - drop the focus state with it,
  // or it would point at markers that are no longer on the map
  const clearPinFocus = () => {
    hoveredPin = null;
    selectedPin = null;
    selectedPulse.hide();
    hideSelectedOverlay();
    // the open list addresses pins that are about to be discarded
    eventStacks.closeList();
  };

  const setSelectedPin = (pin: DetailPin | null) => {
    if (selectedPin === pin) return;
    const previous = selectedPin;
    selectedPin = pin;
    refreshPinFocus(previous);
    refreshPinFocus(pin);
  };

  // Selection is held as a point, not a pin.
  const findPinForPoint = (point: VehicleMapDataPoint) =>
    detailPins.find((pin) =>
      pin.point.recordid != null && point.recordid != null
        ? pin.point.recordid === point.recordid
        : pin.point.timestamp === point.timestamp
    ) ?? null;

  const setSelectedEvent = (point: VehicleMapDataPoint | null) => {
    const pin = point ? findPinForPoint(point) : null;
    setSelectedPin(pin);
    if (!point) {
      selectedPulse.hide();
      hideSelectedOverlay();
      return;
    }
    selectedPulse.showAt(point);
    showSelectedOverlay(point);
  };

  // Drives the highlight from whatever the page says is selected, so the
  // list and the map stay in step: picking a row in the event list marks
  // the spot on the map, and closing the popup (null) clears it. A marker
  // click has already marked itself by the time this arrives, and lands as
  // a no-op.
  // Only a pick reaches here, so the camera move is click-only - hovering a
  // row or a route does not centre the map. See eventCamera.
  const eventCamera = createEventCamera(map);

  const highlightEventPoint = (point: VehicleMapDataPoint | null) => {
    setSelectedEvent(point);
    // clearing the highlight is the event popup closing, which is the cue
    // to put the view back where focusing the first event found it
    if (point) eventCamera.focusOn(point);
    else eventCamera.restore();
  };
  // while this doesn't match the open set, pins stay hidden and the
  // animation reveals them
  let detailAnimationCompleteFor = "";
  const detailSetSignature = (keys: Set<string>) =>
    Array.from(keys).sort().join("|");

  // a pin belongs on the map only if its trip is the one being read - or if
  // nothing has been singled out, in which case the whole set shows
  const pinIsVisible = (pin: { tripKey: string }) =>
    activeDetailTripKey === null ||
    !detailTripKeys.has(activeDetailTripKey) ||
    pin.tripKey === activeDetailTripKey;

  const isTripHidden = (tripKey: string) =>
    activeDetailTripKey !== null &&
    detailTripKeys.has(activeDetailTripKey) &&
    detailTripKeys.has(tripKey) &&
    tripKey !== activeDetailTripKey;

  const showAllDetailPins = () => {
    // a chunked build may still be running - this tells it to create the
    // rest already on the map rather than hidden
    detailPinsRevealed = true;
    detailPins.forEach((pin) => {
      if (pin.shown) return;
      if (!pinIsVisible(pin)) return;
      setPinOnMap(pin, true);
      pin.shown = true;
    });
  };

  const applyDetailPinVisibility = () => {
    detailPins.forEach((pin) => {
      if (!pin.shown) return;
      setPinOnMap(pin, pinIsVisible(pin));
    });
  };

  // singles out one trip from the open set without redrawing anything
  const setActiveDetailTrip = (tripKey: string | null) => {
    activeDetailTripKey = tripKey;
    applyRouteStyling();
    applyDetailPinVisibility();
  };

  // Hovering a pin with others underneath opens the list of what is there.
  // Enlarging whichever is on top says which one a click would take, but
  // gives no way to reach the rest - and nothing is drawn on the map for
  // this, so a position with a single event behaves exactly as before.
  const eventStacks = createEventStackController<DetailPin>({
    google,
    map,
    timezone,
    getPins: () => detailPins,
    isPinVisible: (pin) => pin.shown && pinIsVisible(pin),
    labelFor: eventLabel,
    // a stack here can mix vehicles, so the row has to say whose event it is
    detailFor: (point) =>
      entryByTripKey.get(findPinForPoint(point)?.tripKey ?? "")?.trip
        .vehicleFullLabel,
    onHoverPoint: (point) =>
      setHoveredPin(point ? findPinForPoint(point) : null),
    onSelectPoint: (point) => {
      const pin = findPinForPoint(point);
      setSelectedEvent(point);
      if (pin) onTripPointClick?.(pin.tripKey, point, "pin");
    },
  });

  // --- overlap clusters
  // off by default - detection is expensive at fleet scale and most
  // selections don't need it. the vehicle legend panel carries the toggle.
  let overlapsEnabled = false;

  // the zoom the badges on screen were worked out for. Detection walks every
  // metre of every route, so it is not something to re-run on each wheel
  // click - only when the integer zoom lands somewhere new, which is the
  // only thing that changes the answer.
  let clusterZoomLevel: number | null = null;

  const computeClusters = () => {
    if (!overlapsEnabled) return [];
    const zoom = Math.round(map.getZoom() ?? 12);
    clusterZoomLevel = zoom;
    return detectOverlapClusters(
      entries.map((entry) => ({
        tripKey: entry.tripKey,
        path: entry.rawPath,
      })),
      {
        maxSpanMeters: overlapSpanForZoom(
          zoom,
          map.getCenter()?.lat?.() ?? 0
        ),
      }
    );
  };

  let clusters = computeClusters();

  const clusterTripInfo = (cluster: OverlapCluster): ClusterListTrip[] =>
    cluster.tripKeys
      .map((tripKey) => entryByTripKey.get(tripKey))
      .filter((entry): entry is (typeof entries)[number] => !!entry)
      .map((entry) => ({
        tripKey: entry.tripKey,
        vehicleLabel: entry.trip.vehicleFullLabel,
        tripNumber: entry.trip.tripNumber,
        color: entry.color,
      }));

  let clusterMarkers: any[] = [];
  // The two halves of looking at a badge, and they are deliberately not the
  // same thing. The list says which trips are here and is reached by
  // hovering; the lens pulls the routes apart so you can see them and is
  // reached by clicking. Clicking doesn't take the list away - it is the
  // same badge being looked at more closely.
  let activeClusterList: any | null = null;
  let activeClusterListId: string | null = null;
  let activeClusterLens: any | null = null;

  const closeClusterList = () => {
    activeClusterList?.setMap(null);
    activeClusterList = null;
    activeClusterListId = null;
  };

  const closeClusterLens = () => {
    activeClusterLens?.setMap(null);
    activeClusterLens = null;
  };

  const closeLens = () => {
    closeClusterList();
    closeClusterLens();
  };

  const openClusterList = (cluster: OverlapCluster) => {
    // re-entering the same badge shouldn't rebuild the popup underneath the
    // cursor - it would throw away a scroll position and any drag
    if (activeClusterListId === cluster.id) return;
    closeClusterList();
    activeClusterListId = cluster.id;
    const overlay = createClusterAssetListOverlay({
      google,
      map,
      position: { lat: cluster.lat, lng: cluster.lng },
      trips: clusterTripInfo(cluster),
      onViewDetails: (tripKey) => {
        const entry = entryByTripKey.get(tripKey);
        closeLens();
        if (!entry) return;
        if (onClusterTripSelect) onClusterTripSelect(tripKey);
        else onTripPointClick?.(tripKey, entry.trip.points[0]);
      },
      // picking a row out of the list highlights that route behind it, so
      // you can see which one you're about to open
      onHoverTrip: (tripKey) => setHighlightedTrip(tripKey),
      onClose: closeLens,
    });
    overlay.setMap(map);
    activeClusterList = overlay;
  };

  // The magnifier: a zoomed-in map of this one spot with the routes nudged
  // apart, which is the only way to tell which line is which where several
  // run down the same road. The list beside it already names them, so here
  // it is the circle on its own.
  const openClusterLens = (cluster: OverlapCluster) => {
    closeClusterLens();
    const overlay = createClusterLensOverlay({
      google,
      mainMap: map,
      position: { lat: cluster.lat, lng: cluster.lng },
      showPanel: false,
      trips: cluster.tripKeys
        .map((tripKey) => entryByTripKey.get(tripKey))
        .filter((entry): entry is (typeof entries)[number] => !!entry)
        .map((entry) => ({
          tripKey: entry.tripKey,
          color: entry.color,
          rawPath: entry.rawPath,
          dayLabel: entry.trip.vehicleFullLabel,
          tripNumber: entry.trip.tripNumber,
        })),
      onTripClick: (tripKey) => {
        const entry = entryByTripKey.get(tripKey);
        closeLens();
        if (!entry) return;
        if (onClusterTripSelect) onClusterTripSelect(tripKey);
        else onTripPointClick?.(tripKey, entry.trip.points[0]);
      },
      onClose: closeClusterLens,
    });
    overlay.setMap(map);
    activeClusterLens = overlay;
  };

  // Going closer, rather than framing every trip under the badge. Those
  // trips may each run the width of the city, and fitting all of them is the
  // opposite of looking at the one place where they meet. Two levels in is
  // also what splits the badge into the ones it was standing for.
  const zoomToCluster = (cluster: OverlapCluster) => {
    map.panTo({ lat: cluster.lat, lng: cluster.lng });
    map.setZoom(Math.min(18, (map.getZoom() ?? 12) + 2));
  };

  const backgroundClickListener = map.addListener("click", () => {
    closeLens();
    eventStacks.closeList();
  });

  // What counts as one place changes with the zoom, so the badges are worked
  // out again once the map settles somewhere new. Idle rather than
  // zoom_changed, so a pinch or a double-click costs one pass instead of
  // one per frame, and panning costs none at all.
  const overlapZoomListener = map.addListener("idle", () => {
    if (!overlapsEnabled || isAnimating) return;
    if (Math.round(map.getZoom() ?? 12) === clusterZoomLevel) return;
    clusters = computeClusters();
    if (detailTripKeys.size === 0) createClusterMarkers();
  });

  // Badges only belong on a finished picture. The draw doesn't start until
  // the map goes idle, so anything asking for badges before then - the
  // overlaps toggle being applied as the renderer is built, or path
  // alignment landing early - would put them up over routes that haven't
  // been drawn yet.
  let hasCompletedDraw = false;

  const createClusterMarkers = () => {
    clusterMarkers.forEach((marker) => marker.setMap(null));
    clusterMarkers = [];
    if (!hasCompletedDraw) return;
    clusterMarkers = clusters.map((cluster) => {
      const marker = new google.maps.Marker({
        position: { lat: cluster.lat, lng: cluster.lng },
        map: detailTripKeys.size > 0 ? null : map,
        icon: clusterBadgeIcon(google, cluster.tripKeys.length),
        zIndex: 200,
      });
      // Hovering puts the list up and leaves it up - the same as the fleet
      // map's asset clusters, which is where people have already learnt
      // this. A tooltip that died with the cursor couldn't be read, let
      // alone moved into and clicked.
      marker.addListener("mouseover", () => openClusterList(cluster));
      marker.addListener("click", (e: any) => {
        e?.domEvent?.stopPropagation?.();
        openClusterList(cluster);
        openClusterLens(cluster);
        zoomToCluster(cluster);
      });
      return marker;
    });
  };

  // --- shared-clock animation
  // every route is drawn against one wall-clock timeline, so vehicles that
  // were out at the same time unfold together instead of one after another.
  // pacing is given as a total run time plus a step count, so duration and
  // smoothness can be tuned independently. more steps than there are
  // animation points just means some frames don't advance, so the two are
  // kept roughly in step.
  type TimelinePace = { steps: number; durationMs: number };
  // the reveal on load is brisk - it's only showing you what's there
  const INITIAL_PACE: TimelinePace = { steps: 140, durationMs: 6000 };
  // Replay All is the one you actually sit and watch: 90s at 1x, slow
  // enough to follow one vehicle through its day. the speed buttons divide
  // it, so 10x still brings it back to about nine seconds.
  const REPLAY_PACE: TimelinePace = { steps: 450, durationMs: 90000 };
  let animationToken = 0;
  let isAnimating = false;
  let labelMarkers: any[] = [];

  // shared between the timeline replay and the detail-view draw below -
  // only one ever runs at a time, so one gate covers both. holds the loop
  // between steps rather than tearing anything down, so resuming picks up
  // exactly where it left off.
  let isReplayPaused = false;
  let replayResumeWaiters: (() => void)[] = [];
  const waitIfReplayPaused = () => {
    if (!isReplayPaused) return Promise.resolve();
    return new Promise<void>((resolve) => replayResumeWaiters.push(resolve));
  };
  const pauseReplay = () => {
    isReplayPaused = true;
  };
  const resumeReplay = () => {
    if (!isReplayPaused) return;
    isReplayPaused = false;
    const waiters = replayResumeWaiters;
    replayResumeWaiters = [];
    waiters.forEach((resolve) => resolve());
  };
  // an indefinitely-paused loop would otherwise never see a cancel token
  // change, so anything that ends a run also releases the pause
  const releaseReplayPause = () => {
    isReplayPaused = false;
    const waiters = replayResumeWaiters;
    replayResumeWaiters = [];
    waiters.forEach((resolve) => resolve());
  };

  const clearLabels = () => {
    labelMarkers.forEach((marker) => marker.setMap(null));
    labelMarkers = [];
  };

  const timelineBounds = () => {
    let start = Infinity;
    let end = -Infinity;
    entries.forEach(({ trip }) => {
      trip.path.forEach((point) => {
        if (point.timestamp < start) start = point.timestamp;
        if (point.timestamp > end) end = point.timestamp;
      });
    });
    return { start, end };
  };

  const playTimelineAnimation = async (
    getSpeedMultiplier: () => number,
    // clock is the real unix timestamp the animation is currently laying
    // down - the shared wall clock every drawn trip is being timed against
    onProgress?: (percent: number, clock?: number) => void,
    pace: TimelinePace = INITIAL_PACE
  ) => {
    const { steps: timelineSteps, durationMs } = pace;
    animationToken += 1;
    const token = animationToken;
    isAnimating = true;
    releaseReplayPause();

    clusterMarkers.forEach((marker) => marker.setMap(null));
    clusterMarkers = [];
    closeLens();
    clearLabels();
    entries.forEach(({ polyline }) => polyline.setPath([]));
    // a replay starting from the detail view would otherwise leave that
    // trip's pins floating over routes that have been wiped back to empty
    clearPinFocus();
    detailPins.forEach((pin) => setPinOnMap(pin, false));
    detailPins = [];

    const { start, end } = timelineBounds();
    if (!isFinite(start) || !isFinite(end) || end <= start) {
      entries.forEach(({ polyline, rawPath }) => polyline.setPath(rawPath));
      hasCompletedDraw = true;
      createClusterMarkers();
      return;
    }

    labelMarkers = entries.map(({ trip, color }) =>
      new google.maps.Marker({
        position: { lat: trip.path[0].lat, lng: trip.path[0].lng },
        map: null,
        icon: vehicleLabelIcon(google, trip.vehicleLabel, color),
        zIndex: 300,
      })
    );

    // the clock only moves forward, so each route resumes scanning where it
    // stopped instead of walking its timestamps again every frame
    const drawnCounts = entries.map(() => 0);

    for (let step = 1; step <= timelineSteps; step++) {
      if (token !== animationToken || cancelled) return;
      const clock = start + ((end - start) * step) / timelineSteps;

      entries.forEach(({ polyline, animPath, animTimestamps }, entryIndex) => {
        let count = drawnCounts[entryIndex];
        while (count < animTimestamps.length && animTimestamps[count] <= clock)
          count += 1;

        // at this pace most routes stand still on most frames - redrawing
        // an unchanged path is the expensive part, so don't
        if (count === drawnCounts[entryIndex]) return;
        drawnCounts[entryIndex] = count;

        const label = labelMarkers[entryIndex];
        if (count < 2) {
          polyline.setPath([]);
          label?.setMap(null);
          return;
        }
        polyline.setPath(animPath.slice(0, count));
        // the label only rides along while this vehicle is actually moving
        const isStillDriving = count < animPath.length;
        label?.setPosition(animPath[count - 1]);
        label?.setMap(isStillDriving ? map : null);
      });

      onProgress?.(Math.round((step / timelineSteps) * 100), clock);
      await wait(durationMs / timelineSteps / getSpeedMultiplier());
      await waitIfReplayPaused();
      if (token !== animationToken || cancelled) return;
    }

    if (token !== animationToken || cancelled) return;
    isAnimating = false;
    flushPendingPathUpgrades();
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    clearLabels();
    hasCompletedDraw = true;
    createClusterMarkers();
  };

  // Puts the finished picture up with no animation at all. Used when the
  // renderer is rebuilt while a trip's detail is open - an event replay
  // ending, a refresh landing - where replaying the whole fleet and then
  // re-opening the detail is not what going back to that screen means.
  const drawWithoutAnimating = () => {
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    // the pins were held back for a draw that is not going to happen
    showAllDetailPins();
    if (detailTripKeys.size > 0)
      detailAnimationCompleteFor = detailSetSignature(detailTripKeys);
    hasCompletedDraw = true;
    createClusterMarkers();
  };

  const stopTimelineAnimation = () => {
    animationToken += 1;
    isAnimating = false;
    releaseReplayPause();
    flushPendingPathUpgrades();
    clearLabels();
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    // skipping the animation still counts as arriving at the finished view
    hasCompletedDraw = true;
    createClusterMarkers();
  };

  // a null filteredStatusCodes means no filter applied, so pin everything;
  // an empty array is a real "pin nothing".
  // every trip in the set gets its events pinned, so a whole asset's day can
  // be read at once.
  // Routes are drawn from the raw GPS fixes the moment the data lands, then
  // each one is swapped for its road-aligned path as that comes back. The
  // alternative - holding the whole map back until every trip has been
  // through snap-to-road and directions - is minutes at fleet scale.
  const applyPathUpgrade = (tripKey: string, path: TimedPathPoint[]) => {
    const entry = entryByTripKey.get(tripKey);
    if (!entry || path.length < 2) return;

    entry.rawPath = path.map((point) => ({ lat: point.lat, lng: point.lng }));
    const animPoints = thinForAnimation(path);
    entry.animPath = animPoints.map((point) => ({
      lat: point.lat,
      lng: point.lng,
    }));
    entry.animTimestamps = animPoints.map((point) => point.timestamp);
    entry.trip.path = path;
    entry.polyline.setPath(entry.rawPath);
  };

  // a draw loop tracks how far along each path it is, and the road-aligned
  // path has a different number of points - swapping underneath it would
  // make the route jump. Upgrades that land mid-draw wait for it to finish.
  const pendingPathUpgrades = new Map<string, TimedPathPoint[]>();

  const flushPendingPathUpgrades = () => {
    pendingPathUpgrades.forEach((path, tripKey) =>
      applyPathUpgrade(tripKey, path)
    );
    pendingPathUpgrades.clear();
  };

  // `immediate` is a route-view switch rather than a background upgrade.
  // A snap-to-road path landing mid-draw is held so the animation does not
  // jump, but a switch has already restyled the line, and holding the
  // geometry back leaves it drawn on the view the user just turned off.
  const updateTripPath = (
    tripKey: string,
    path: TimedPathPoint[],
    immediate = false
  ) => {
    if (isAnimating && !immediate) {
      pendingPathUpgrades.set(tripKey, path);
      return;
    }
    applyPathUpgrade(tripKey, path);
  };

  // overlaps shift once routes snap to the road, so they're recounted after
  // the upgrades land rather than per route - the detection is O(n squared).
  // a no-op while overlaps are toggled off, since computeClusters returns
  // empty without doing the work.
  const refreshOverlapClusters = () => {
    if (isAnimating) return;
    flushPendingPathUpgrades();
    clusters = computeClusters();
    if (detailTripKeys.size === 0 && !isAnimating) createClusterMarkers();
  };

  // the vehicle legend panel owns this toggle - off by default since
  // detection cost isn't worth paying until someone actually wants to see it
  const setOverlapsEnabled = (enabled: boolean) => {
    if (overlapsEnabled === enabled) return;
    overlapsEnabled = enabled;
    if (!enabled) closeLens();
    clusters = computeClusters();
    if (detailTripKeys.size === 0 && !isAnimating) createClusterMarkers();
  };

  // in-flight chunked builds are abandoned when a new selection arrives
  let detailPinBuildToken = 0;
  // set once the pins are meant to be on the map - a chunked build still
  // running has to honour a reveal that already happened
  let detailPinsRevealed = false;
  // whether the open selection is being handled in heavy mode. Drives the
  // ordering: heavy selections keep every pin off the map until the routes
  // have finished drawing, then wave them in.
  let detailPinsHeavy = false;

  const setDetailedTrips = (
    tripKeys: string[],
    filteredStatusCodes: StatusCodeFilter = null
  ) => {
    // a trip opening or closing frames its own route, so a view saved
    // before that is no longer anywhere to go back to
    eventCamera.forget();
    // abandons any chunked build still running for the previous selection
    detailPinBuildToken += 1;
    const buildToken = detailPinBuildToken;
    const nextKeys = new Set(tripKeys);
    if (detailSetSignature(nextKeys) !== detailSetSignature(detailTripKeys))
      detailAnimationCompleteFor = "";
    detailTripKeys = nextKeys;
    if (activeDetailTripKey && !nextKeys.has(activeDetailTripKey))
      activeDetailTripKey = null;
    // detail mode changes dimming and interactivity, and the highlight
    // effect runs before this one, so re-apply here
    applyRouteStyling();

    // the markers these point at are about to be discarded
    clearPinFocus();
    detailPins.forEach((pin) => setPinOnMap(pin, false));
    detailPins = [];

    const inDetailMode = nextKeys.size > 0;
    // nothing to disambiguate once the map is down to a chosen few
    clusterMarkers.forEach((marker) => marker.setMap(inDetailMode ? null : map));
    if (inDetailMode) closeLens();
    if (!inDetailMode) return;

    // hold pins back while the routes are still drawing; once they're done
    // (e.g. just a filter change) the new selection appears straight away
    // this renderer has no "show immediately" override, so holding is
    // simply "the routes have not been drawn for this selection yet" -
    // which is already the ordering a heavy selection needs
    const holdForAnimation =
      detailAnimationCompleteFor !== detailSetSignature(nextKeys);

    // Flattened first so the heavy-selection decision is made on the real
    // total across every open trip, not per trip.
    const pinnable: {
      tripKey: string;
      point: VehicleMapDataPoint;
      pointIndex: number;
    }[] = [];
    nextKeys.forEach((tripKey) => {
      const entry = entryByTripKey.get(tripKey);
      if (!entry) return;
      entry.trip.points.forEach((point, pointIndex) => {
        if (statusCodeIsPinned(filteredStatusCodes, point.statuscode))
          pinnable.push({ tripKey, point, pointIndex });
      });
    });

    
    const runs = groupIntoRuns(pinnable, (point) => !isMovingEvent(point));

    // heavy selections additionally show nothing DURING the draw and wave
    // in once it finishes - see the reveal cap in the animation loop
    detailPinsHeavy = runs.length > HEAVY_TRIP_PIN_COUNT;
    detailPinsRevealed = !holdForAnimation;

    const addPin = (
      run: (typeof pinnable)[number][],
      { visible, drop }: { visible: boolean; drop: boolean }
    ) => {
      // the last of the run carries the marker - Matt's "a stopped icon at
      // the last one" - so the pin sits where the asset actually was when
      // it moved off again
      const { tripKey, point, pointIndex } = run[run.length - 1];
      const onMap = visible && pinIsVisible({ tripKey });
      const marker = new google.maps.Marker({
        position: { lat: point.latitude, lng: point.longitude },
        map: onMap ? map : null,
        icon: eventMarkerIcon(point),
        zIndex: PIN_Z_INDEX_BY_FOCUS.none,
        ...(drop && onMap ? { animation: google.maps.Animation.DROP } : {}),
      });
      const pin: DetailPin = {
        marker,
        tripKey,
        pointIndex,
        shown: onMap,
        point,
        focus: "none",
      };

      if (run.length > 1) {
        pin.runPoints = run.map((item) => item.point);
        // to the next thing that happened, not to the last identical
        // report - the asset was still sitting there until it moved
        const after = entryByTripKey.get(tripKey)?.trip.points[pointIndex + 1];
        const endsAt = after ? after.timestamp : point.timestamp;
        pin.runLabel = formatDuration(endsAt - run[0].point.timestamp);
        pin.runLabelMarker = new google.maps.Marker({
          position: { lat: point.latitude, lng: point.longitude },
          map: onMap ? map : null,
          clickable: false,
          icon: runLabelIcon(pin.runLabel),
          zIndex: PIN_Z_INDEX_BY_FOCUS.none,
        });
      }
      // Google only delivers the pointer events to the topmost marker, so
      // the pin that grows here is exactly the pin a click would land on -
      // which is the whole point where events are stacked.
      marker.addListener("mouseover", () => {
        setHoveredPin(pin);
        // a pin standing for a run of identical events offers what it
        // stands for before anything about the map around it
        if (pin.runPoints && eventStacks.openRun(pin.runPoints)) return;
        // others sitting underneath: show what is there. On hover rather
        // than on click - clicking already opens the detail panel and is
        // only going to carry more, and this is a "what am I looking at"
        // question, not an action.
        eventStacks.openStackFor(pin);
      });
      marker.addListener("mouseout", () => {
        if (hoveredPin === pin) setHoveredPin(null);
        eventStacks.scheduleClose();
      });
      marker.addListener("click", () => {
        // marked straight away rather than waiting for the page to come
        // back with its selection - the pulse should be up by the time the
        // detail panel is
        setSelectedEvent(pin.point);
        onTripPointClick?.(tripKey, point, "pin");
      });
      detailPins.push(pin);
    };

    // the ordinary case, unchanged: one pass, every pin dropping in together
    if (!detailPinsHeavy) {
      runs.forEach((run) =>
        addPin(run, { visible: !holdForAnimation, drop: !holdForAnimation })
      );
      return;
    }

    // Heavy selection. Building thousands of markers in one pass blocks the
    // main thread long enough to stall the route animation that is meant to
    // be playing, so the build is spread a chunk per frame and runs
    // alongside the draw; whichever reveal applies brings them in after.
    let buildIndex = 0;
    const buildChunk = () => {
      if (buildToken !== detailPinBuildToken) return;
      const end = Math.min(buildIndex + PIN_BUILD_CHUNK, runs.length);
      for (; buildIndex < end; buildIndex++) {
        addPin(runs[buildIndex], {
          visible: detailPinsRevealed,
          drop: false,
        });
      }
      if (buildIndex < runs.length) {
        requestAnimationFrame(buildChunk);
        return;
      }
      if (!holdForAnimation) revealDetailPinsInWaves(buildToken);
    };
    requestAnimationFrame(buildChunk);
  };

  // Brings pins in a chunk per frame, each chunk still doing the native drop
  // bounce - the entrance a heavy selection would otherwise go without, at a
  // few dozen concurrent animations instead of thousands.
  const revealDetailPinsInWaves = (buildToken: number) => {
    let cursor = 0;
    const step = () => {
      if (buildToken !== detailPinBuildToken) return;
      const end = Math.min(cursor + PIN_REVEAL_CHUNK, detailPins.length);
      for (; cursor < end; cursor++) {
        const pin = detailPins[cursor];
        if (pin.shown || !pinIsVisible(pin)) continue;
        setPinOnMap(pin, true);
        pin.marker.setAnimation(google.maps.Animation.DROP);
        pin.shown = true;
      }
      if (cursor < detailPins.length) requestAnimationFrame(step);
      else detailPinsRevealed = true;
    };
    requestAnimationFrame(step);
  };

  // --- detail-view route draw. slower than the multi-vehicle reveal, with
  // the same speed control as the replays.
  const DETAIL_STEPS = 160;
  const DETAIL_DURATION_MS = 40000;
  // pins trail the drawing head by this many animation steps, not by real
  // time - a fixed visual lag regardless of how long the trip actually
  // spanned, so pins on a two-hour trip don't wait real minutes to appear
  const DETAIL_PIN_LAG_STEPS = 6;
  let detailAnimationToken = 0;

  // every trip in the set is paced against ONE real wall clock - not
  // against its own step fraction. That distinction is the whole point:
  // two trips from the SAME vehicle never overlap in real time (one ends
  // before the next starts, always), so pacing by real time draws them one
  // after the other automatically, with no special-casing needed. Two
  // trips from DIFFERENT vehicles that really were on the road at the same
  // moment draw together, for the same reason. Fraction-pacing (the old
  // approach) got this backwards: it drew every trip in the set to the
  // same percent-complete regardless of when either one actually
  // happened, which is what made a vehicle's whole day look like it was
  // in two places at once.
  const playDetailAnimation = async (
    tripKeys: string[],
    getSpeedMultiplier: () => number,
    // clock is the real unix timestamp the draw is currently at - the
    // isolated trip's own time when one is singled out (the rest are
    // hidden by isTripHidden below), or the whole open set's real
    // timeline otherwise.
    // speedKph is the asset's reported speed at that moment, and only when
    // a single trip is drawing: a set of them is several assets at once,
    // and "the speed" has no answer then
    onProgress?: (percent: number, clock?: number, speedKph?: number) => void
  ) => {
    const focusedEntries = tripKeys
      .map((tripKey) => entryByTripKey.get(tripKey))
      .filter((entry): entry is (typeof entries)[number] => !!entry);
    if (focusedEntries.length === 0) return;

    // the token check in the loop abandons any previous run
    detailAnimationToken += 1;
    const token = detailAnimationToken;
    releaseReplayPause();

    // an abandoned run returns without tidying up, so anything it left on
    // the map is cleared here rather than at its own end
    entries.forEach(({ polyline }) =>
      polyline.setOptions({ icons: strokeIcons })
    );
    focusedEntries.forEach(({ polyline }) => polyline.setPath([]));

    // isolating one trip narrows the clock to just that trip's own span,
    // so it draws promptly rather than waiting out however much of the
    // rest of the set's timeline came before it
    const activeEntries = focusedEntries.filter(
      ({ tripKey }) => !isTripHidden(tripKey)
    );
    let start = Infinity;
    let end = -Infinity;
    activeEntries.forEach(({ trip }) => {
      trip.path.forEach((point) => {
        if (point.timestamp < start) start = point.timestamp;
        if (point.timestamp > end) end = point.timestamp;
      });
    });

    if (!isFinite(start) || !isFinite(end) || end <= start) {
      // not enough of a real span to pace against - nothing to animate,
      // so just put the finished picture up
      focusedEntries.forEach(({ polyline, rawPath }) => {
        polyline.setPath(rawPath);
        polyline.setOptions({ icons: strokeIcons });
      });
      onProgress?.(100, isFinite(end) ? end : undefined);
      showAllDetailPins();
      detailAnimationCompleteFor = detailSetSignature(detailTripKeys);
      return;
    }

    // resumes scanning where each trip left off rather than rewalking its
    // timestamps every frame - same trick as the shared-clock reveal
    const drawnCounts = new Map(focusedEntries.map(({ tripKey }) => [tripKey, 0]));

    // one trip drawing on its own is one asset, so it has a speed worth
    // showing. The drawn path may be road-aligned geometry, so the speed
    // comes off the reported fixes, walked forward with the clock.
    const speedEntry = activeEntries.length === 1 ? activeEntries[0] : null;
    let speedCursor = 0;
    const speedAtClock = (clock: number) => {
      if (!speedEntry) return undefined;
      const points = speedEntry.trip.points;
      while (
        speedCursor + 1 < points.length &&
        points[speedCursor + 1].timestamp <= clock
      )
        speedCursor += 1;
      return points[speedCursor]?.speedkph;
    };

    for (let step = 1; step <= DETAIL_STEPS; step++) {
      if (token !== detailAnimationToken || cancelled) return;
      const clock = start + ((end - start) * step) / DETAIL_STEPS;
      // pins lag a fixed few steps behind the line, in animation time
      const lagClock =
        start + ((end - start) * Math.max(0, step - DETAIL_PIN_LAG_STEPS)) / DETAIL_STEPS;

      focusedEntries.forEach(
        ({ tripKey, polyline, animPath, animTimestamps, color }) => {
          if (isTripHidden(tripKey)) return;
          let count = drawnCounts.get(tripKey) ?? 0;
          while (count < animTimestamps.length && animTimestamps[count] <= clock)
            count += 1;
          drawnCounts.set(tripKey, count);
          if (count < 2) {
            polyline.setPath([]);
            return;
          }
          polyline.setPath(animPath.slice(0, count));
          polyline.setOptions({ icons: [...strokeIcons, arrowHeadIcon(color)] });
        }
      );

      // reveal pins whose own reported time the (lagged) clock has
      // reached - real time, the same as the line drawing through them.
      // Capped per step: a long stop can report hundreds of events inside
      // one step's slice of the clock, and animating them together is the
      // same stall this pacing exists to avoid. The overflow still satisfies
      // the check next step, so it catches up a chunk at a time.
      let revealedThisStep = 0;
      for (const pin of detailPins) {
        if (revealedThisStep >= PIN_REVEAL_CHUNK) break;
        if (pin.shown) continue;
        const entry = entryByTripKey.get(pin.tripKey);
        const point = entry?.trip.points[pin.pointIndex];
        if (!point || point.timestamp > lagClock) continue;
        pin.shown = true;
        revealedThisStep += 1;
        if (!pinIsVisible(pin)) continue;
        setPinOnMap(pin, true);
        pin.marker.setAnimation(google.maps.Animation.DROP);
      }

      onProgress?.(
        Math.round((step / DETAIL_STEPS) * 100),
        clock,
        speedAtClock(clock)
      );
      await wait(DETAIL_DURATION_MS / DETAIL_STEPS / getSpeedMultiplier());
      await waitIfReplayPaused();
      if (token !== detailAnimationToken || cancelled) return;
    }

    if (token !== detailAnimationToken || cancelled) return;
    focusedEntries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    onProgress?.(100, end, speedAtClock(end));

    // the tail end that was still inside the lag window
    showAllDetailPins();
    detailAnimationCompleteFor = detailSetSignature(detailTripKeys);
  };

  // snaps the route to complete and drops any remaining pins
  const stopDetailAnimation = () => {
    detailAnimationToken += 1;
    releaseReplayPause();
    entries.forEach(({ polyline, rawPath }) => {
      polyline.setPath(rawPath);
      polyline.setOptions({ icons: strokeIcons });
    });
    showAllDetailPins();
    if (detailTripKeys.size > 0)
      detailAnimationCompleteFor = detailSetSignature(detailTripKeys);
  };

  // undoes showAllDetailPins/stopDetailAnimation's "everything shown" -
  // hides every pin and forgets that the draw already completed, so the
  // next playDetailAnimation call does its real reveal-as-the-line-passes
  // animation again instead of finding every pin already marked shown and
  // leaving them sitting there, unmoving, over the redrawing route. Used to
  // give a caller that opens straight into a finished detail view (a
  // compare card) a working Replay button.
  const resetDetailPinsForReplay = () => {
    detailPins.forEach((pin) => {
      pin.shown = false;
      setPinOnMap(pin, false);
    });
    detailAnimationCompleteFor = "";
    clearPinFocus();
  };

  const fitBoundsToTrips = (tripKeys: string[]): Promise<void> => {
    const bounds = new google.maps.LatLngBounds();
    let hasAny = false;
    tripKeys.forEach((tripKey) => {
      const entry = entryByTripKey.get(tripKey);
      if (!entry) return;
      entry.rawPath.forEach((point: any) => {
        bounds.extend(point);
        hasAny = true;
      });
    });
    if (!hasAny) return Promise.resolve();
    return new Promise((resolve) => {
      google.maps.event.addListenerOnce(map, "idle", resolve);
      map.fitBounds(bounds, 60);
    });
  };

  return {
    clear: () => {
      cancelled = true;
      animationToken += 1;
      detailAnimationToken += 1;
      releaseReplayPause();
      google.maps.event.removeListener(backgroundClickListener);
      google.maps.event.removeListener(overlapZoomListener);
      closeLens();
      clearLabels();
      entries.forEach(({ polyline }) => polyline.destroy());
      routeHitLayer.destroy();
      clusterMarkers.forEach((marker) => marker.setMap(null));
      selectedPulse.destroy();
      selectedOverlayMarker?.setMap(null);
      eventStacks.closeList();
      detailPins.forEach((pin) => setPinOnMap(pin, false));
    },
    fitBoundsIfAny: () => {
      if (entries.length === 0) return;
      const bounds = new google.maps.LatLngBounds();
      entries.forEach(({ rawPath }) =>
        rawPath.forEach((point) => bounds.extend(point))
      );
      map.fitBounds(bounds);
    },
    playTimelineAnimation,
    drawWithoutAnimating,
    REPLAY_PACE,
    updateTripPath,
    refreshOverlapClusters,
    setOverlapsEnabled,
    stopTimelineAnimation,
    playDetailAnimation,
    stopDetailAnimation,
    resetDetailPinsForReplay,
    pauseReplay,
    resumeReplay,
    setHighlightedTrip,
    setLineStyle,
    setDetailedTrips,
    setActiveDetailTrip,
    highlightEventPoint,
    fitBoundsToTrips,
    closeLens,
  };
};
